import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { fetchPosts } from "../lib/api";
import type { Post } from "../types/blog";
import background from "../assets/background4.webp";

function BlogPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts()
      .then(setPosts)
      .catch((err) => {
        console.error("❌ Error in BlogPage:", err);
        setError("Failed to load posts");
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>WholeFoods.ai Blog – AI & Sustainable Food Insights</title>
        <meta
          name="description"
          content="Read expert articles on AI in nutrition, sustainable grocery innovation, and food technology. Discover how WholeFoods.ai is shaping the future of food with AI."
        />
        <meta
          name="keywords"
          content="WholeFoods.ai blog, AI in grocery, sustainable food tech, artificial intelligence nutrition, whole foods ai"
        />
        <meta property="og:title" content="WholeFoods.ai Blog" />
        <meta
          property="og:description"
          content="Explore deep insights into AI-powered nutrition and sustainable grocery innovations. WholeFoods.ai blog covers the future of food."
        />
        <meta property="og:image" content="https://wholefoods.ai/blog-og.jpg" />
        <meta property="og:url" content="https://wholefoods.ai/blog" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="WholeFoods.ai Blog" />
        <meta
          name="twitter:description"
          content="Learn how AI is transforming grocery, sustainability, and nutrition."
        />
        <meta
          name="twitter:image"
          content="https://wholefoods.ai/blog-twitter.jpg"
        />
      </Helmet>

      <section className="bg-primary text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            WholeFoods.ai Blog: AI, Grocery & Sustainability
          </h1>
          <p className="text-xl text-white-700">
            Insights at the intersection of artificial intelligence, nutrition,
            and sustainable food.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {loading && (
            <p className="text-center text-gray-600">Loading posts...</p>
          )}
          {error && <p className="text-center text-red-600">{error}</p>}

          {!loading && !error && posts.length === 0 && (
            <p className="text-center text-gray-600">No posts available.</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <img
                    src={background}
                    alt={`AI in food tech: ${post.title}`}
                    className="w-full rounded-md mb-4"
                    loading="lazy"
                  />
                  <p className="text-sm text-gray-500 mb-2">
                    {new Date(post.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                  <h3 className="text-xl font-bold mb-2">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-gray-900 hover:text-primary-600 transition-colors"
                    >
                      {post.title}
                    </Link>
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {post.description || "No description available"}
                  </p>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Read More →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogPage;
